import { httpsCallable } from 'firebase/functions';
import type { DeckIntelligence } from '../types';
import type { OpportunityWeakPointPayload } from './proposeOpportunityFixes';
import { functions } from './firebase';

const RESOLVE_CALLABLE_TIMEOUT_MS = 270_000;

export type ResolveOpportunityWeakPointResponse = {
  ideaSummary: string;
  ideaIntelligence: DeckIntelligence;
  changeSummary: string;
};

export function resolveOpportunityWeakPointErrorMessage(err: unknown): string {
  if (err && typeof err === 'object' && 'message' in err) {
    const message = String((err as { message: string }).message);
    if (message && !message.startsWith('Firebase')) {
      return message;
    }
  }
  if (err && typeof err === 'object' && 'code' in err) {
    const code = String((err as { code: string }).code);
    if (code === 'functions/invalid-argument') {
      return 'Enter a resolution of at least 10 characters.';
    }
    if (code === 'functions/unauthenticated') {
      return 'Sign in to apply the fix.';
    }
    if (code === 'functions/unavailable') {
      return 'Fix service is temporarily unavailable. Try again.';
    }
    if (code === 'functions/deadline-exceeded') {
      return 'Applying the fix timed out. Try again in a moment.';
    }
  }
  return err instanceof Error ? err.message : 'Could not apply the fix.';
}

export async function resolveOpportunityWeakPoint(
  opportunityId: string,
  weakPoint: OpportunityWeakPointPayload,
  resolution: string
): Promise<ResolveOpportunityWeakPointResponse> {
  const fn = httpsCallable<
    { opportunityId: string; weakPoint: OpportunityWeakPointPayload; resolution: string },
    ResolveOpportunityWeakPointResponse
  >(functions, 'resolveOpportunityWeakPoint', { timeout: RESOLVE_CALLABLE_TIMEOUT_MS });
  return (await fn({ opportunityId: opportunityId.trim(), weakPoint, resolution })).data;
}
